/* <details-modal>
  <details>
    <summary aria-haspopup="dialog" aria-label="[label}">
      Size chart
    </summary>
    <div class="modal" role="dialog" aria-modal="true" aria-label="[label]">
      <div class="modal-overlay"></div>
      <div class="modal-content" tabindex="-1">
        [CONTENT]
        <button type="button" aria-label="Close">X</button>
      </div>
    </div>
  </details>
</details-modal> */

class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.isClosing = false;
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.duplicatedBis = null;
    this.duplicatedElement = null;

    this.detailsContainer.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE' && !this.isClosing) {
        this.close();
      }
    });
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));

    this.summaryToggle.setAttribute('role', 'button');
  }

  connectedCallback() {}

  isOpen() {
    return (
      this.detailsContainer.hasAttribute('open') ||
      this.duplicatedElement?.querySelector('details')?.hasAttribute('open')
    );
  }

  onSummaryClick(event) {
    event.preventDefault();
    if (this.isClosing) return;
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (
      (!this.contains(event.target) && !this.duplicatedElement?.contains(event.target)) ||
      event.target.classList.contains('modal-overlay')
    ) {
      event.preventDefault();
      if (this.isClosing) return;
      this.close(false);
    }
  }

  open(event) {
    if (this.isClosing || this.isOpen()) return;

    const backInStock = this.closest('back-in-stock');

    if (backInStock) {
      this.duplicatedBis = backInStock.cloneNode(true);
      this.duplicatedElement = this.duplicatedBis.querySelector('details-modal');
    } else {
      this.duplicatedElement = this.cloneNode(true);
    }

    // Create a duplicate of this element and append to body
    this.duplicatedElement.classList.add('open');
    document.body.appendChild(backInStock ? this.duplicatedBis : this.duplicatedElement);

    // Get references from the duplicated element
    const duplicatedDetails = this.duplicatedElement.querySelector('details');
    const duplicatedContent = this.duplicatedElement.querySelector('.modal-content');
    const duplicatedCloseButton = this.duplicatedElement.querySelector('button.modal-close-button');

    // Set up event listeners on the duplicated element
    this.duplicatedElement.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE' && !this.isClosing) {
        this.close();
      }
    });
    duplicatedCloseButton.addEventListener('click', this.close.bind(this));

    /* iOS Safari fix – give the browser one paint frame in the
       off-screen state before we start the transition */
    duplicatedDetails.setAttribute('open', true);
    duplicatedContent.classList.add('modal-content--no-transition');
    duplicatedContent.style.transform = 'translate3d(100%,0,0)';
    duplicatedContent.style.opacity = '0';
    duplicatedContent.getBoundingClientRect(); // force reflow
    requestAnimationFrame(() => {
      duplicatedContent.classList.remove('modal-content--no-transition');
      duplicatedContent.style.transform = '';
      duplicatedContent.style.opacity = '';
    });

    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('!overflow-hidden');

    duplicatedCloseButton.focus();

    trapFocus(duplicatedContent, this.duplicatedElement.querySelector('input:not([type="hidden"])'));
  }

  _performCleanup() {
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('!overflow-hidden');

    // Remove the duplicated element from body
    if (this.duplicatedElement) {
      document.body.removeChild(this.duplicatedBis ?? this.duplicatedElement);
      this.duplicatedElement = null;
    }
  }

  close(focusToggle = true) {
    if (this.isClosing || !this.isOpen()) {
      return;
    }
    this.isClosing = true;

    removeTrapFocus(focusToggle ? this.summaryToggle : null);

    const modalContent =
      this.duplicatedElement?.querySelector('.modal-content') || this.detailsContainer.querySelector('.modal-content');

    if (!modalContent) {
      this.detailsContainer.removeAttribute('open');
      this._performCleanup();
      this.isClosing = false;
      // Restore focus to the summary toggle
      if (focusToggle && this.summaryToggle) {
        this.summaryToggle.focus();
      }
      return;
    }

    const onModalCloseTransitionEnd = () => {
      this.detailsContainer.removeAttribute('open');
      this.detailsContainer.classList.remove('is-closing');
      if (this.duplicatedElement) {
        this.duplicatedElement.querySelector('details').classList.remove('is-closing');
      }
      this._performCleanup();
      this.isClosing = false;
      // Restore focus to the summary toggle after cleanup
      if (focusToggle && this.summaryToggle) {
        this.summaryToggle.focus();
      }
    };

    modalContent.addEventListener('transitionend', onModalCloseTransitionEnd, { once: true });
    this.detailsContainer.classList.add('is-closing');
    if (this.duplicatedElement) {
      this.duplicatedElement.querySelector('details').classList.add('is-closing');
    }

    // Fallback timeout in case transitionend doesn't fire
    setTimeout(() => {
      if (this.detailsContainer.classList.contains('is-closing')) {
        modalContent.removeEventListener('transitionend', onModalCloseTransitionEnd); // Clean up listener
        this.detailsContainer.removeAttribute('open');
        this.detailsContainer.classList.remove('is-closing');
        if (this.duplicatedElement) {
          this.duplicatedElement.querySelector('details').classList.remove('is-closing');
        }
        this._performCleanup();
        this.isClosing = false;
        // Restore focus to the summary toggle after cleanup
        if (focusToggle && this.summaryToggle) {
          this.summaryToggle.focus();
        }
      }
    }, 350); // Slightly longer than the CSS transition (300ms)
  }
}

customElements.define('details-modal', DetailsModal);
